import { createFileRoute, redirect } from '@tanstack/react-router';
import { SelectionFrame } from '@/components/engagement-requirements/selection-frame';
import { VendorIntelligenceScreen } from '@/components/vendor-intelligence/vendor-intelligence-screen';
import { viStepFromSlug } from '@/components/vendor-intelligence/vi-logic';

/**
 * Engagement → Selection → Vendor intelligence → one step (T-0272;
 * E24-design §2):
 *
 *   …/selection/vendor-intelligence/vendors | ratings | shortlist | report
 *
 * A step the tab does not have sends the reader to the bare URL, which
 * lands on the first step not done. `?product=<id>&module=<id>` on the
 * Ratings step opens that cell's panel (T-0276: a link from the area
 * matrix into the grid).
 */
export interface ViStepSearch {
  product?: string;
  module?: string;
}

const text = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

export const Route = createFileRoute(
  '/engagements/$engagementId/selection_/vendor-intelligence/$step',
)({
  validateSearch: (search: Record<string, unknown>): ViStepSearch => {
    const product = text(search['product']);
    const module = text(search['module']);
    return { ...(product ? { product } : {}), ...(module ? { module } : {}) };
  },
  beforeLoad: ({ params }) => {
    if (!viStepFromSlug(params.step)) {
      throw redirect({
        to: '/engagements/$engagementId/selection/vendor-intelligence',
        params: { engagementId: params.engagementId },
        replace: true,
      });
    }
  },
  component: VendorIntelligenceStepRoute,
});

function VendorIntelligenceStepRoute() {
  const { engagementId, step } = Route.useParams();
  const { product, module } = Route.useSearch();
  return (
    <SelectionFrame>
      <VendorIntelligenceScreen
        key={engagementId}
        engagementId={engagementId}
        step={viStepFromSlug(step) ?? 'vendors'}
        openCell={product && module ? { productId: product, moduleId: module } : null}
      />
    </SelectionFrame>
  );
}
